import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPinIcon, CalendarIcon, BeakerIcon, BuildingOfficeIcon, UserIcon, PhoneIcon, TagIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';

const PointDetailPage = () => {
  const { pointId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [pointData, setBedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    fetchPointDetails();
  }, [pointId]);

  const fetchPointDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPointPublic(pointId);
      
      if (response.success) {
        setBedData(response.data);
      } else {
        setError(response.message || 'Failed to fetch point details');
        toast.error(response.message || 'Failed to fetch point details');
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while fetching point details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReportClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setShowReportModal(true);
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    toast.success('Thank you for your report. It has been submitted for review.');
  };

  const handleLoginRedirect = () => {
    setShowLoginPrompt(false);
    // Store the current URL to redirect back after login
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/login');
  };

  const InfoCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center mb-4">
        {Icon && <Icon className="h-6 w-6 text-green-600 mr-2" />}
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start py-2">
      {Icon && <Icon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />}
      <div className="flex-1">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 mt-1">{value || 'N/A'}</dd>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading point details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Point Details</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/points')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Points
          </button>
        </div>
      </div>
    );
  }

  if (!pointData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Point Not Found</h2>
          <p className="text-gray-600 mb-4">The requested point could not be found.</p>
          <button
            onClick={() => navigate('/points')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Points
          </button>
        </div>
      </div>
    );
  }

  const { point, watershed, site } = pointData;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => navigate(-1)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Back
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">Monitoring Point Details</li>
            </ol>
          </nav>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {point.parameter} - Point {point.point_id}
              </h1>
              <p className="text-gray-600 mt-1">
                Water quality monitoring point details
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Report Button */}
              <button
                onClick={handleReportClick}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors shadow-sm"
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Report Issue
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Point Information */}
            <InfoCard title="Monitoring Point Information" icon={BeakerIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Point ID" value={point.point_id} icon={TagIcon} />
                <InfoRow label="Parameter" value={point.parameter} icon={TagIcon} />
                <InfoRow label="Description" value={point.description || 'N/A'} />
                <InfoRow label="Measurement Units" value={point.measurement_units || 'N/A'} />
                <InfoRow
                  label="Coordinates"
                  value={point.coordinates ?
                    `${point.coordinates.latitude}°N, ${point.coordinates.longitude}°E` :
                    'N/A'
                  }
                  icon={MapPinIcon}
                />
                <InfoRow label="Created" value={formatDate(point.created_at)} />
                <InfoRow label="Last Updated" value={formatDate(point.updated_at)} />
              </dl>
            </InfoCard>

            {/* Sensor Information */}
            <InfoCard title="Sensor Metadata" icon={BeakerIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Sensor ID" value={point.sensor_id || 'N/A'} />
                <InfoRow label="Sensor Type" value={point.sensor_type || 'N/A'} />
                <InfoRow label="Calibration Date" value={formatDate(point.calibration_date)} icon={CalendarIcon} />
                <InfoRow label="Data Frequency" value={point.data_frequency || 'N/A'} />
                <InfoRow label="Power Source" value={point.power_source || 'N/A'} />
                <InfoRow label="Communication Type" value={point.communication_type || 'N/A'} />
                <InfoRow label="Active" value={point.active ? 'Yes' : 'No'} />
              </dl>
            </InfoCard>

            {/* Site Information */}
            {site && (
              <InfoCard title="Site Information" icon={MapPinIcon}>
                <dl className="divide-y divide-gray-100">
                  <InfoRow label="Site Name" value={site.name} />
                  <InfoRow label="Description" value={site.description || 'N/A'} />
                  <InfoRow label="Waterbody Type" value={site.waterbody_type || 'N/A'} />
                  <InfoRow label="Flow Regime" value={site.flow_regime || 'N/A'} />
                  <InfoRow label="Elevation" value={site.elevation ? `${site.elevation} meters` : 'N/A'} />
                </dl>
              </InfoCard>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Watershed Information */}
            {watershed && (
              <InfoCard title="Watershed Information" icon={BuildingOfficeIcon}>
                <dl className="divide-y divide-gray-100">
                  <InfoRow label="Watershed Name" value={watershed.name} />
                  <InfoRow label="Description" value={watershed.description || 'N/A'} />
                  <InfoRow label="Created By" value={watershed.created_by || 'N/A'} icon={UserIcon} />
                  <InfoRow label="Total Area" value={watershed.area ? `${watershed.area} sq km` : 'N/A'} />
                  <InfoRow label="Location" value={`${watershed.address || ''}${watershed.state ? `, ${watershed.state}` : ''}, ${watershed.country || ''}`} />
                  <InfoRow
                    label="Coordinates"
                    value={watershed.coordinates && (watershed.coordinates.latitude || watershed.coordinates.longitude) ?
                      `${watershed.coordinates.latitude}°N, ${watershed.coordinates.longitude}°E` :
                      'N/A'
                    }
                  />
                  <InfoRow label="Established" value={formatDate(watershed.created_at)} />
                </dl>
              </InfoCard>
            )}

            {/* QR Code Section */}
            {point.qr_code && (
              <InfoCard title="QR Code">
                <div className="text-center">
                  <img
                    src={point.qr_code}
                    alt="Point QR Code"
                    className="mx-auto h-32 w-32 border border-gray-200 rounded"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Scan to view this point's details
                  </p>
                </div>
              </InfoCard>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/points')}
            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors font-medium"
          >
            ← Back to Points
          </button>
        </div>
      </div>

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Report an Issue"
        size="xl"
      >
        <ReportForm
          pointData={pointData}
          onSuccess={handleReportSuccess}
          onCancel={() => setShowReportModal(false)}
        />
      </Modal>

      {/* Login Prompt Modal */}
      <Modal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Login Required"
        size="md"
        footer={
          <>
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLoginRedirect}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Login to Continue
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            You need to be logged in to report issues. Please log in to continue.
          </p>
          <p className="text-sm text-gray-500">
            After logging in, you'll be redirected back to this page to submit your report.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default PointDetailPage;